import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useProductCapabilities } from "@/lib/useProductCapabilities";
import { PageHeader, Table, Btn, Badge } from "@/components/UI";
import { usePagination, ListPagination } from "@/components/ListPagination";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ExplicitFilterBar } from "@/components/common/ExplicitFilterBar";
import { ProductFormModal } from "@/components/products/ProductFormModal";
import { AuthImage } from "@/components/products/AuthImage";
import { fmtNum, toQuery } from "@/lib/products";
import { apiError } from "@/lib/customers";

const INITIAL = { q: "", code: "", active: "" };
const FIELDS = [
  { key: "q", label: "Ürün Adı", width: "w-64" },
  { key: "code", label: "Ürün Kodu", width: "w-40" },
  { key: "active", label: "Durum", type: "select", width: "w-36", options: [{ value: "", label: "Tümü" }, { value: "true", label: "Aktif" }, { value: "false", label: "Pasif" }] },
];

const mainPhoto = (p) => (p.photos || []).find((x) => x.is_primary) || (p.photos || [])[0];

export default function ProductsPage() {
  const nav = useNavigate();
  const caps = useProductCapabilities();
  const [rows, setRows] = useState([]);
  const [loadErr, setLoadErr] = useState("");
  const [filters, setFilters] = useState(INITIAL);
  const [modal, setModal] = useState({ open: false, product: null });
  const load = (f = filters) => api.get(`/products${toQuery(f)}`).then((r) => { setRows(r); setLoadErr(""); })
    .catch((e) => setLoadErr(apiError(e, "Ürünler yüklenemedi")));
  useEffect(() => { load(INITIAL); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pg = usePagination(rows, "products", 100);
  const applyFilters = (f) => { setFilters(f); load(f); };

  const remove = async (p) => {
    if (!window.confirm(`${p.code} — ${p.name} silinsin mi?\n\nÜrün Çöp Kutusu'na taşınır, stok geçmişi korunur.`)) return;
    try { await api.del(`/products/${p.id}`); load(); } catch (e) { alert(apiError(e, "Silme başarısız")); }
  };

  return (
    <div data-testid="products-page">
      <PageHeader title="Ürünler" description={`Ürün / envanter ana kartları — ${rows.length} kayıt`}
        actions={caps.product_create && <Btn onClick={() => setModal({ open: true, product: null })} data-testid="new-product-btn"><Plus size={14} /> Yeni Ürün</Btn>} />
      {loadErr && <div className="mb-3 text-sm text-rose-600" data-testid="products-load-error">{loadErr}</div>}
      <ExplicitFilterBar fields={FIELDS} initial={INITIAL} onApply={applyFilters} pdfPath="/products/pdf" pdfName="Urun_Listesi.pdf" testid="prod-filter" />
      <Table testid="products-table" onRowClick={(p) => nav(`/products/${p.id}`)} empty="Ürün bulunamadı"
        columns={[
          { label: "", render: (p) => mainPhoto(p)
            ? <AuthImage path={`/products/${p.id}/photos/${mainPhoto(p).id}?variant=thumb`} className="w-10 h-10 rounded-md" />
            : <div className="w-10 h-10 rounded-md bg-slate-100" /> },
          { label: "Kod", render: (p) => <span className="font-mono text-xs" data-testid={`prod-row-code-${p.id}`}>{p.code}</span> },
          { label: "Ürün", render: (p) => <span className="font-medium" data-testid={`prod-row-name-${p.id}`}>{p.name}</span> },
          { label: "Alış", render: (p) => `${fmtNum(p.purchase_price)} ${p.purchase_currency}` },
          { label: "Bayi", render: (p) => fmtNum(p.price_bayi) },
          { label: "Nokta", render: (p) => fmtNum(p.price_nokta) },
          { label: "Nihai T.", render: (p) => fmtNum(p.price_nihai_tuketici) },
          { label: "İnternet", render: (p) => fmtNum(p.price_internet) },
          { label: "Durum", render: (p) => <Badge level={p.active ? "ok" : "inactive"}>{p.active ? "Aktif" : "Pasif"}</Badge> },
          { label: "İşlem", render: (p) => (
            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              {caps.product_edit && <Btn size="sm" variant="secondary" onClick={() => setModal({ open: true, product: p })} data-testid={`edit-prod-${p.id}`}><Pencil size={12} /> Düzenle</Btn>}
              {caps.product_delete && <Btn size="sm" variant="danger" onClick={() => remove(p)} data-testid={`delete-prod-${p.id}`}><Trash2 size={12} /> Sil</Btn>}
            </div>
          ) },
        ]}
        rows={pg.paged} />
      <ListPagination pagination={pg} testid="products-pagination" />
      <ProductFormModal open={modal.open} product={modal.product} onClose={() => setModal({ open: false, product: null })}
        onSaved={() => { setModal({ open: false, product: null }); load(); }} />
    </div>
  );
}
